// src/pages/Privacy.jsx (or wherever you keep legal pages)
import React from "react";
import "./LegalPage.css";

export default function Privacy() {
  return (
    <div className="legal-container">
      <div className="legal-card">
        <h1 className="legal-title">Privacy Policy</h1>

        <div className="legal-section">
          <p>
            Welcome to ActivityBuddy (&quot;ActivityBuddy&quot;, &quot;we&quot;, &quot;our&quot;, &quot;us&quot;).
            This Privacy Policy explains how we collect, use, disclose, and
            protect your information when you use our platform.
          </p>
          <p>
            By accessing or using ActivityBuddy, you consent to this Privacy
            Policy. If you do not agree, please discontinue use of the platform.
          </p>
        </div>

        <div className="legal-section">
          <h2>1. What this policy covers</h2>
          <p>This Privacy Policy applies to:</p>
          <ul className="legal-list">
            <li>The ActivityBuddy website and application</li>
            <li>Communications (email, notifications, customer support)</li>
            <li>Data related to bookings, activities, and user interactions</li>
          </ul>
          <p>
            It does <strong>not</strong> apply to third-party websites,
            services, or platforms that Hosts or Customers may use
            independently (for example, Instagram, Google Maps, or individual
            venues).
          </p>
        </div>

        <div className="legal-section">
          <h2>2. Information we collect</h2>
          <p>
            We collect information in three categories: information you provide,
            information collected automatically, and information from third
            parties.
          </p>
        </div>

        <div className="legal-section">
          <h3>2.1 Information you provide</h3>
          <h4>Your account</h4>
          <ul className="legal-list">
            <li>Full name</li>
            <li>Email address</li>
            <li>Username and display name</li>
            <li>Profile and listing information (bio, photos, activities, etc)</li>
          </ul>

          <h4>Bookings</h4>
          <ul className="legal-list">
            <li>Names of Customers</li>
            <li>Requested time, date, and details of activities</li>
            <li>Customer message / intro (optional)</li>
            <li>Host notes (optional)</li>
            <li>Decision status (accepted / declined / completed)</li>
          </ul>

          <h4>Payments</h4>
          <p>Payment processing is handled by Stripe.</p>
          <p>We do not store:</p>
          <ul className="legal-list">
            <li>Card numbers</li>
            <li>Bank account numbers</li>
            <li>Routing numbers</li>
          </ul>

          <h4>Support &amp; communications</h4>
          <ul className="legal-list">
            <li>Emails, messages, and attachments you send us</li>
            <li>Reports or safety concerns you raise</li>
            <li>Feedback and reviews</li>
          </ul>
        </div>

        <h3>2.1.1 Public profile & listings</h3>
        <p>
          Hosts create public-facing profiles and activity listings. This data is intentionally visible to Customers and may include:
        </p>
        <ul className="legal-list">
          <li>Display name</li>
          <li>Username and listing URL</li>
          <li>Profile photo</li>
          <li>Activity titles and descriptions</li>
          <li>Pricing and fee breakdowns</li>
          <li>Availability and scheduling details</li>
        </ul>
        <p>
          Public profile data is viewable by anyone who visits your ActivityBuddy page or booking link.
        </p>
        <p>
          We treat this as public information because it is part of the service you choose to publish.
        </p>
        

        <div className="legal-section">
          <h3>2.2 Information collected automatically</h3>
          <p>
            We may collect certain technical and usage information automatically
            when you use ActivityBuddy, including:
          </p>
          <ul className="legal-list">
            <li>IP address</li>
            <li>Device type and browser type</li>
            <li>Operating system</li>
            <li>Pages viewed and time spent on the site</li>
            <li>Referring URLs</li>
            <li>Basic analytics events (e.g., clicks, errors)</li>
          </ul>
          <p>
            We do not use cross-site tracking, fingerprinting, or invasive
            advertising tools.
          </p>
        </div>

        <div className="legal-section">
          <h3>2.3 Information from third parties</h3>
          <p>We may receive information from:</p>
          <ul className="legal-list">
            <li>Stripe (payment status, payout state, compliance flags)</li>
            <li>Authentication providers</li>
            <li>Analytics providers</li>
            <li>Law enforcement, when legally required</li>
          </ul>
          <p>We do not purchase data from data brokers.</p>
        </div>

        <div className="legal-section">
          <h3>2.4 Pricing, fees & transaction data</h3>
          <p>We may receive information from:</p>
          <ul className="legal-list">
            <li>Activity prices set by Hosts</li>
            <li>Platform fees and calculated totals</li>
            <li>Stripe payment IDs and payout status</li>
            <li>Charge and authorization timestamps</li>
            <li>Refund or dispute metadata</li>
            <li>Customer and Host billing summaries</li>
          </ul>
          <p>This information is used to display accurate totals, generate receipts, calculate earnings, prevent fraud, support audits, comply with Stripe’s requirements, and resolve disputes.</p>
        </div>

        <div className="legal-section">
          <h2>3. How we use your information</h2>
          <p>We use your information to:</p>

          <h3>3.1 Provide and improve the platform</h3>
          <ul className="legal-list">
            <li>Process and manage bookings</li>
            <li>Enable Hosts to offer and manage activities</li>
            <li>Display relevant activity and booking details</li>
            <li>Ensure secure payments and payouts</li>
            <li>Maintain and improve website performance and usability</li>
          </ul>

          <h3>3.2 Maintain safety and integrity</h3>
          <ul className="legal-list">
            <li>Help verify user identities and reduce fraud</li>
            <li>Investigate reports and policy violations</li>
            <li>Detect suspicious activity, abuse, and spam</li>
            <li>Enforce our Terms &amp; Conditions</li>
          </ul>

          <h3>3.3 Communicate with you</h3>
          <ul className="legal-list">
            <li>Send booking confirmations and status updates</li>
            <li>Send payment or payout-related notices</li>
            <li>Respond to questions, feedback, and support requests</li>
            <li>Notify you of changes to policies or features</li>
          </ul>

          <h3>3.4 Legal compliance</h3>
          <ul className="legal-list">
            <li>Respond to legal requests and obligations</li>
            <li>Resolve disputes and enforce our agreements</li>
            <li>Support compliance requirements from our providers (e.g., Stripe)</li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>4. Sharing your information</h2>
          <p>
            We only share information when it&apos;s necessary to operate
            ActivityBuddy or when we are legally required to do so.
          </p>

          <h3>4.1 With Hosts and Customers</h3>
          <p>To facilitate bookings, we may share:</p>
          <ul className="legal-list">
            <li>Customer&apos;s full name</li>
            <li>Customer&apos;s intro message (if provided)</li>
            <li>Host&apos;s activity details</li>
            <li>Booking time, date, and location</li>
            <li>Host&apos;s display name and username</li>
          </ul>
          <p>We do not share unnecessary private data.</p>

          <h3>4.2 With service providers</h3>
          <p>We work with trusted third-party service providers such as:</p>
          <ul className="legal-list">
            <li>Stripe (payments and payouts)</li>
            <li>Email providers (transactional email delivery)</li>
            <li>Analytics providers (aggregate usage statistics)</li>
            <li>Hosting and infrastructure providers</li>
          </ul>
          <p>
            These providers are contractually obligated to protect your
            information and use it only for the services they provide to us.
          </p>

          <h3>4.3 For legal reasons</h3>
          <p>
            We may disclose information when we believe in good faith that such
            disclosure is:
          </p>
          <ul className="legal-list">
            <li>Required by law, subpoena, or court order</li>
            <li>
              Necessary to investigate or address potential violations of our
              Terms
            </li>
            <li>
              Needed to protect the safety, rights, or property of ActivityBuddy
              or our users
            </li>
            <li>
              Appropriate in response to credible safety threats or suspected
              illegal activities
            </li>
          </ul>
          <p>We do not sell your personal data.</p>
        </div>

        <div className="legal-section">
          <h2>5. Cookies and tracking</h2>
          <p>We use cookies and similar technologies to:</p>
          <ul className="legal-list">
            <li>Support core site functionality</li>
            <li>Maintain login sessions</li>
            <li>Help power booking flows and state (e.g., date/time selection)</li>
            <li>Measure basic, non-personalized analytics</li>
          </ul>
          <p>We do not use:</p>
          <ul className="legal-list">
            <li>Behavioral advertising cookies</li>
            <li>Cross-site tracking for ad networks</li>
            <li>Third-party advertising pixels</li>
          </ul>
          <p>
            You can disable cookies through your browser settings, but some
            features of ActivityBuddy may not function properly if cookies are
            disabled.
          </p>
        </div>

        <div className="legal-section">
          <h2>6. Data retention</h2>
          <p>We retain data only for as long as reasonably necessary:</p>
          <ul className="legal-list">
            <li>Booking records: up to 5 years for disputes and audits</li>
            <li>Account data: for as long as your account remains active</li>
            <li>
              Payment-related data: as required by Stripe and applicable
              financial laws
            </li>
            <li>Support communications: for as long as needed to handle issues</li>
            <li>
              Safety reports: may be retained longer to help protect the
              community
            </li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>7. Data security</h2>
          <p>
            We use technical and organizational safeguards to protect your
            information, including:
          </p>
          <ul className="legal-list">
            <li>TLS encryption (HTTPS) for data in transit</li>
            <li>Restricted access to production systems</li>
            <li>Secure database configuration</li>
            <li>Use of Stripe&apos;s security for payment information</li>
          </ul>
          <p>
            However, no online service can guarantee absolute security. By using
            ActivityBuddy, you understand and accept these inherent risks.
          </p>
        </div>

        <div className="legal-section">
          <h2>8. Children&apos;s privacy</h2>
          <p>
            ActivityBuddy is not intended for individuals under 18 years old. We
            do not knowingly collect information from minors.
          </p>
          <p>
            If we become aware that we have collected information from someone
            under 18, we will delete that information.
          </p>
        </div>

        <div className="legal-section">
          <h2>9. International users</h2>
          <p>
            ActivityBuddy is based in the United States. If you access the
            platform from outside the U.S., you understand and agree that your
            information may be transferred to, stored in, and processed in the
            United States, where data protection laws may differ from those in
            your jurisdiction.
          </p>
        </div>

        <div className="legal-section">
          <h2>10. Your rights</h2>
          <p>
            Depending on where you live, you may have certain rights regarding
            your personal data, including the right to:
          </p>
          <ul className="legal-list">
            <li>Access the personal information we hold about you</li>
            <li>Request corrections to inaccurate information</li>
            <li>Request deletion of your account and associated data</li>
            <li>Request a copy of your personal data</li>
            <li>
              Object to or restrict certain types of processing, where
              applicable by law
            </li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a className="legal-link" href="mailto:support@activitybuddy.com">
              support@activitybuddy.com
            </a>
            . We may ask you to verify your identity before fulfilling your
            request.
          </p>
        </div>

        <div className="legal-section">
          <h2>11. Account deletion</h2>
          <p>
            You may request deletion of your account by contacting support. When
            we delete an account:
          </p>
          <ul className="legal-list">
            <li>Profile and login information is removed or anonymized</li>
            <li>
              Booking history may be retained where necessary for legal,
              financial, or dispute resolution reasons
            </li>
            <li>
              Reviews or feedback may be anonymized rather than fully deleted
            </li>
            <li>
              Certain records associated with Stripe may continue to exist under
              Stripe&apos;s own retention policies
            </li>
          </ul>
        </div>

        <div className="legal-section">
          <h2>12. Automated analysis</h2>
          <p>
            We may use automated tools (for example, fraud or spam detection) to
            help:
          </p>
          <ul className="legal-list">
            <li>Identify suspicious behavior or abnormal patterns</li>
            <li>Detect abuse, harassment, or policy violations</li>
            <li>Support safety investigations</li>
          </ul>
          <p>
            We do not use AI or automated tools to analyze private conversations
            for advertising or behavioral profiling.
          </p>
        </div>

        <div className="legal-section">
          <h2>13. Safety and reports</h2>
          <p>
            If a user reports unsafe behavior or concerning conduct, we may:
          </p>
          <ul className="legal-list">
            <li>Document and review the report</li>
            <li>Review relevant booking and account history</li>
            <li>Restrict, suspend, or terminate accounts</li>
            <li>Contact involved parties when appropriate</li>
            <li>
              Notify law enforcement where we believe there may be a risk of
              harm or where we are legally required to do so
            </li>
          </ul>
          <p>
            We reserve the right to act proactively when we believe harm could
            occur to users or the broader community.
          </p>
        </div>

        <div className="legal-section">
          <h2>14. Changes to this Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will update the &quot;Last Updated&quot; date at the top of this
            page.
          </p>
          <p>
            Your continued use of ActivityBuddy after changes are posted means
            you accept the updated Privacy Policy.
          </p>
        </div>

        <div className="legal-section">
          <h2>15. Contact us</h2>
          <p>
            If you have questions, concerns, or requests about this Privacy
            Policy or your data, contact us at support@activitybuddy.com
          </p>
        </div>
      </div>
    </div>
  );
}